import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import Spinner from '../components/Spinner';
import { toast } from 'react-toastify';
import { FaEye, FaEdit, FaSync, FaPlus } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import { formatDateTime } from '../utils/formatDate';

const Couriers = () => {
  // State for list and meta
  const [couriers, setCouriers] = useState([]);
  const [meta, setMeta] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for modals and form
  const [isAddEditOpen, setAddEditOpen] = useState(false);
  const [isViewOpen, setViewOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);

  const [selectedCourier, setSelectedCourier] = useState(null);
  const [formData, setFormData] = useState({
    username: '', email: '', code: '', phone: '', enabled: true,
    platform: '', addressLine1: '', addressLine2: '', city: '', zipCode: ''
  });

  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Fetch couriers list
  const fetchCouriers = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/api/courier?page=${page}`);
      if (!res.ok) throw new Error('Failed to load data');
      const json = await res.json();
      setCouriers(json.data || []);
      setMeta({
        currentPage: json.meta.currentPage,
        totalPages: json.meta.totalPages,
        totalItems: json.meta.totalItems,
        itemsPerPage: json.meta.itemsPerPage
      });
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCouriers();
  }, []);

  // Pagination handler
  const handlePageChange = (page) => fetchCouriers(page);

  // Open Add Courier modal
  const handleAdd = () => {
    setSelectedCourier(null);
    setFormData({ username: '', email: '', code: '', phone: '', enabled: true, platform: '', addressLine1: '', addressLine2: '', city: '', zipCode: '' });
    setAddEditOpen(true);
  };

  // Open Edit Courier modal
  const handleEdit = (courier) => {
    setSelectedCourier(courier);
    setFormData({
      username: courier.username,
      email: courier.email,
      code: courier.code,
      phone: courier.phone,
      enabled: courier.enabled,
      platform: courier.platform,
      addressLine1: courier.addressLine1,
      addressLine2: courier.addressLine2,
      city: courier.city,
      zipCode: courier.zipCode
    });
    setAddEditOpen(true);
  };

  // Open View Courier modal
  const handleView = (courier) => {
    setSelectedCourier(courier);
    setViewOpen(true);
  };

  // Open Delete Courier confirmation
  const handleDeleteConfirm = (courier) => {
    setSelectedCourier(courier);
    setDeleteOpen(true);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Submit Add/Edit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = selectedCourier
        ? `${apiUrl}/api/courier/${selectedCourier.id}`
        : `${apiUrl}/api/courier`;
      const method = selectedCourier ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Save failed');
      const data = await res.json();
      toast.success(selectedCourier ? 'Updated successfully' : 'Created successfully');
      fetchCouriers(meta.currentPage);
      setAddEditOpen(false);
      // Show credentials for new courier
      if (!selectedCourier && data.api_url && data.api_token) {
        setSelectedCourier(data);
        setViewOpen(true);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Confirm deletion
  const handleDelete = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/courier/${selectedCourier.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Deleted successfully');
      fetchCouriers(meta.currentPage);
      setDeleteOpen(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Regenerate API credentials
  const handleRegenerate = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/courier/${selectedCourier.id}/api-credentials`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed to regenerate');
      const creds = await res.json();
      setSelectedCourier(prev => ({ ...prev, ...creds }));
      toast.success('Credentials regenerated');
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800">Couriers Management</h2>
          <button
            className="btn gap-2 bg-[#002e63] text-white hover:bg-[#012145]"
            onClick={handleAdd}
          >
            <FaPlus className="text-lg" />
            Add Courier
          </button>
        </div>

        {error && (
          <div className="alert alert-error mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center mt-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="bg-base-100 rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table table-zebra">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-gray-600 font-semibold">Name</th>
                    <th className="text-gray-600 font-semibold">Code</th>
                    <th className="text-gray-600 font-semibold">Status</th>
                    <th className="text-gray-600 font-semibold">Added</th>
                    <th className="text-gray-600 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {couriers.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="font-medium cursor-pointer" onClick={() => handleView(c)}>
                        {c.username}
                      </td>
                      <td>{c.code}</td>
                      <td>
                        <span className={`badge ${c.enabled ? 'badge-success' : 'badge-error'} text-white`}>
                          {c.enabled ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{formatDateTime(c.created_at)}</td>
                      <td>
                        <div className="flex justify-end gap-2">
                          <button
                            className="btn btn-ghost btn-xs text-info"
                            onClick={() => handleView(c)}
                          >
                            <FaEye className="text-lg text-blue-800" />
                          </button>
                          <button
                            className="btn btn-ghost btn-xs text-success"
                            onClick={() => handleEdit(c)}
                          >
                            <FaEdit className="text-lg" />
                          </button>
                          <button
                            className="btn btn-ghost btn-xs text-error"
                            onClick={() => handleDeleteConfirm(c)}
                          >
                            <MdDelete className="text-lg" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-6">
          <Pagination
            currentPage={meta.currentPage}
            totalPages={meta.totalPages}
            totalItems={meta.totalItems}
            itemsPerPage={meta.itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>

        {/* Add/Edit Modal */}
        {isAddEditOpen && (
          <div className="modal modal-open">
            <div className="modal-box max-w-2xl">
              <h3 className="font-bold text-2xl mb-6">
                {selectedCourier ? 'Edit Courier' : 'Create New Courier'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Form Fields */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Username</span>
                    </label>
                    <input
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      required
                      className="input input-bordered"
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Email</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input input-bordered"
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Code</span>
                    </label>
                    <input
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                      required
                      className="input input-bordered"
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Phone</span>
                    </label>
                    <input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input input-bordered"
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Platform</span>
                    </label>
                    <input
                      name="platform"
                      value={formData.platform}
                      onChange={handleChange}
                      className="input input-bordered"
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Status</span>
                    </label>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="checkbox"
                        name="enabled"
                        checked={formData.enabled}
                        onChange={handleChange}
                        className="toggle toggle-success"
                      />
                      <span className="text-sm">{formData.enabled ? 'Enabled' : 'Disabled'}</span>
                    </div>
                  </div>

                  <div className="md:col-span-2 form-control">
                    <label className="label">
                      <span className="label-text font-medium">Address Line 1</span>
                    </label>
                    <input
                      name="addressLine1"
                      value={formData.addressLine1}
                      onChange={handleChange}
                      className="input input-bordered"
                    />
                  </div>

                  <div className="md:col-span-2 form-control">
                    <label className="label">
                      <span className="label-text font-medium">Address Line 2</span>
                    </label>
                    <input
                      name="addressLine2"
                      value={formData.addressLine2}
                      onChange={handleChange}
                      className="input input-bordered"
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">City</span>
                    </label>
                    <input
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="input input-bordered"
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Zip Code</span>
                    </label>
                    <input
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                      className="input input-bordered"
                    />
                  </div>
                </div>

                <div className="modal-action mt-6">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setAddEditOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn bg-[#002e63] text-white hover:bg-[#012145]">
                    {selectedCourier ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Modal */}
        {isViewOpen && selectedCourier && (
          <div className="modal modal-open">
            <div className="modal-box max-w-xl">
              <h3 className="font-bold text-2xl mb-4">Courier Details</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <DetailItem label="Username" value={selectedCourier.username} />
                  <DetailItem label="Email" value={selectedCourier.email} />
                  <DetailItem label="Code" value={selectedCourier.code} />
                  <DetailItem label="Phone" value={selectedCourier.phone} />
                  <DetailItem label="Status" value={selectedCourier.enabled ? 'Active' : 'Inactive'} />
                  <DetailItem label="Platform" value={selectedCourier.platform} />
                </div>

                <div className="border-t pt-4">
                  <DetailItem
                    label="Address"
                    value={`${selectedCourier.addressLine1}, ${selectedCourier.addressLine2}, 
                   ${selectedCourier.city}, ${selectedCourier.zipCode}`}
                    fullWidth
                  />
                  <div className='text-blue-800'>
                    <DetailItem
                      label="API URL"
                      value={`https://courierapi-el52ndtqhq-uc.a.run.app/${selectedCourier.api_url}`}
                      copyable
                      fullWidth
                    />
                  </div>
                  <div className='text-blue-800'>
                    <DetailItem
                      label="API Token"
                      value={selectedCourier.api_token}
                      copyable
                      fullWidth
                      breakAll
                    />
                  </div>
                  <DetailItem
                    label="Created At"
                    value={formatDateTime(selectedCourier.created_at)}
                  />
                </div>
              </div>

              <div className="modal-action mt-6">
                <button
                  className="btn btn-warning gap-2"
                  onClick={handleRegenerate}
                >
                  <FaSync />
                  Regenerate Credentials
                </button>
                <button className="btn" onClick={() => setViewOpen(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Delete Modal */}
        {isDeleteOpen && selectedCourier && (
          <div className="modal modal-open">
            <div className="modal-box text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-red-100 p-4 rounded-full">
                  <MdDelete className="text-4xl text-red-600" />
                </div>
              </div>
              <h3 className="font-bold text-2xl mb-2">Confirm Deletion</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete courier <span className="font-bold text-gray-800">{selectedCourier.username}</span>? This action cannot be undone.
              </p>
              <div className="flex justify-center gap-4">
                <button
                  className="btn btn-ghost border border-gray-300 px-8"
                  onClick={() => setDeleteOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-error px-8 text-white"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

// Helper component for view modal details
const DetailItem = ({ label, value, fullWidth = false, breakAll = false, copyable = false }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(value)
      .then(() => toast.success('Copied to clipboard!'))
      .catch(() => toast.error('Failed to copy'))
  };

  return (
    <div className={`form-control ${fullWidth ? 'col-span-2' : ''}`}>
      <label className="label py-1">
        <span className="label-text font-medium text-gray-600">{label}</span>
      </label>
      <div className={`flex items-center gap-2 ${breakAll ? 'break-all' : ''}`}>
        <span className="text-gray-800 flex-1">{value || '-'}</span>
        {copyable && (
          <button
            onClick={handleCopy}
            className="btn btn-ghost btn-xs p-1 hover:bg-gray-100 rounded"
            title="Copy to clipboard"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default Couriers;